import { CartAddEvent } from '@theme/events';

/**
 * <hawg-carousel>
 *
 * A flame/chrome product carousel for the SHOWCASE theme. Handles arrow scrolling and an
 * optional one-click quick-add (single-variant products) that adds straight to the cart and
 * opens the drawer — mirrors the add flow in assets/product-bundle.js. Cards also pick up the
 * holographic tilt from holo-cards.js (which targets `.hawg-card`).
 */
class HawgCarousel extends HTMLElement {
  #track = null;
  #prev = null;
  #next = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-hc-track]');
    this.#prev = this.querySelector('[data-hc-prev]');
    this.#next = this.querySelector('[data-hc-next]');

    this.addEventListener('click', this.#onClick);
    if (this.#track) {
      this.#track.addEventListener('scroll', this.#updateArrows, { passive: true });
      window.addEventListener('resize', this.#updateArrows, { passive: true });
      requestAnimationFrame(this.#updateArrows);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#updateArrows);
  }

  #onClick = (e) => {
    const add = e.target.closest('[data-hc-add]');
    if (add) {
      e.preventDefault();
      this.#quickAdd(add);
      return;
    }
    if (e.target.closest('[data-hc-prev]')) this.#scrollByCards(-1);
    if (e.target.closest('[data-hc-next]')) this.#scrollByCards(1);
  };

  #scrollByCards(direction) {
    if (!this.#track) return;
    const card = this.#track.querySelector('.hawg-card');
    const step = card ? card.getBoundingClientRect().width + 18 : this.#track.clientWidth * 0.8;
    this.#track.scrollBy({ left: step * direction * 1.4, behavior: 'smooth' });
  }

  #updateArrows = () => {
    if (!this.#track || !this.#prev || !this.#next) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    this.#prev.disabled = scrollLeft <= 2;
    this.#next.disabled = scrollLeft >= scrollWidth - clientWidth - 2;
  };

  async #quickAdd(btn) {
    const id = btn.dataset.variantId;
    if (!id) return;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const body = { items: [{ id: Number(id), quantity: 1 }] };
    if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

    const url = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        this.#flash(btn, false);
        return;
      }
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'hawg-carousel', { source: 'hawg-carousel', sections: data.sections })
      );
      this.#flash(btn, true);
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (e) {
      this.#flash(btn, false);
    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }

  #flash(btn, ok) {
    btn.classList.add(ok ? 'hawg-card__add--ok' : 'hawg-card__add--err');
    setTimeout(() => btn.classList.remove('hawg-card__add--ok', 'hawg-card__add--err'), 1500);
  }
}

if (!customElements.get('hawg-carousel')) {
  customElements.define('hawg-carousel', HawgCarousel);
}
